import { COUNTRIES } from '../../lib/countries'
import { Select } from '../../ui/Select'
import { TextArea } from '../../ui/TextArea'
import { TextField } from '../../ui/TextField'
import { userFacingMessage } from '../../api/errors'
import { useUpdateProfile, type Profile, type ProfileUpdate, type ReportLanguage } from '../../api/queries/profile'
import { ReadOnlyRow, SettingsCard, useDraft } from './SettingsCard'
import styles from './Settings.module.css'
import { COMPANY_SIZES, INDUSTRIES, REPORT_LANGUAGES, asOptions } from './options'

const COUNTRY_OPTIONS = asOptions(COUNTRIES)
const SIZE_OPTIONS = asOptions(COMPANY_SIZES)
const INDUSTRY_OPTIONS = asOptions(INDUSTRIES)

/** Every card saves through the same hook; only the fields differ. */
function useCardSave(done: () => void) {
  const update = useUpdateProfile()
  return {
    save: (patch: ProfileUpdate) => update.mutate(patch, { onSuccess: done }),
    saving: update.isPending,
    error: update.isError ? userFacingMessage(update.error) : null,
  }
}

export function PersonalCard({ profile }: { profile: Profile }) {
  const d = useDraft({
    full_name: profile.full_name ?? '',
    position: profile.position ?? '',
    linkedin_url: profile.linkedin_url ?? '',
    timezone: profile.timezone ?? '',
  })
  const { save, saving, error } = useCardSave(d.done)

  return (
    <SettingsCard
      title="Personal"
      description="Your name and how to reach you."
      summary={[profile.full_name, profile.position, profile.timezone].filter(Boolean) as string[]}
      editing={d.editing}
      onEdit={d.start}
      onCancel={d.cancel}
      onSave={() => save(d.draft)}
      saving={saving}
      error={error}
    >
      <TextField
        label="Name"
        value={d.draft.full_name}
        onChange={(e) => d.set('full_name', e.target.value)}
        disabled={!d.editing}
        placeholder="Your full name"
      />
      {/* Email is the identity the session is issued against, so it is shown and
          not edited: changing it here would not change who can sign in. */}
      <ReadOnlyRow label="Email" value={profile.email} />
      <TextField
        label="Position"
        value={d.draft.position}
        onChange={(e) => d.set('position', e.target.value)}
        disabled={!d.editing}
        placeholder="e.g. Head of Sales"
      />
      <TextField
        label="LinkedIn"
        value={d.draft.linkedin_url}
        onChange={(e) => d.set('linkedin_url', e.target.value)}
        disabled={!d.editing}
        placeholder="https://www.linkedin.com/in/…"
      />
      <TextField
        label="Timezone"
        value={d.draft.timezone}
        onChange={(e) => d.set('timezone', e.target.value)}
        disabled={!d.editing}
        placeholder="e.g. Europe/Warsaw"
      />
    </SettingsCard>
  )
}

export function CompanyCard({ profile }: { profile: Profile }) {
  const d = useDraft({
    company_name: profile.company_name ?? '',
    company_size: profile.company_size ?? '',
    industry: profile.industry ?? '',
    company_website: profile.company_website ?? '',
    company_country: profile.company_country ?? '',
  })
  const { save, saving, error } = useCardSave(d.done)

  return (
    <SettingsCard
      title="Company"
      description="Where you work. This helps us tailor YourDealPulse to you."
      summary={[profile.company_name, profile.company_size, profile.company_country].filter(Boolean) as string[]}
      editing={d.editing}
      onEdit={d.start}
      onCancel={d.cancel}
      onSave={() => save(d.draft)}
      saving={saving}
      error={error}
    >
      <TextField
        label="Company name"
        value={d.draft.company_name}
        onChange={(e) => d.set('company_name', e.target.value)}
        disabled={!d.editing}
        placeholder="Company name"
      />
      <Select
        label="Company size"
        options={SIZE_OPTIONS}
        value={d.draft.company_size}
        onChange={(e) => d.set('company_size', e.target.value)}
        disabled={!d.editing}
      />
      <Select
        label="Industry"
        options={INDUSTRY_OPTIONS}
        value={d.draft.industry}
        onChange={(e) => d.set('industry', e.target.value)}
        disabled={!d.editing}
      />
      <TextField
        label="Website"
        value={d.draft.company_website}
        onChange={(e) => d.set('company_website', e.target.value)}
        disabled={!d.editing}
        placeholder="https://…"
      />
      <Select
        label="Country"
        options={COUNTRY_OPTIONS}
        value={d.draft.company_country}
        onChange={(e) => d.set('company_country', e.target.value)}
        disabled={!d.editing}
      />
    </SettingsCard>
  )
}

/** The stored value is the backend's code; the chip shows what the user chose. */
function languageLabel(value: string | null | undefined): string {
  return REPORT_LANGUAGES.find((l) => l.value === (value ?? 'en'))?.label ?? 'English'
}

export function CallsCard({ profile }: { profile: Profile }) {
  const d = useDraft({
    team: (profile.team_members ?? []).join('\n'),
    report_language: (profile.report_language ?? 'en') as ReportLanguage,
  })
  const { save, saving, error } = useCardSave(d.done)

  return (
    <SettingsCard
      title="Calls"
      description="How YourDealPulse reads your calls and writes them up."
      summary={[
        // "0 teammates" is worth saying: an empty team list is why a colleague's
        // name turns up in a deal's contact list, and the card is where that is
        // fixed.
        `${profile.team_members?.length ?? 0} ${(profile.team_members?.length ?? 0) === 1 ? 'teammate' : 'teammates'}`,
        `Summaries in ${languageLabel(profile.report_language)}`,
      ]}
      editing={d.editing}
      onEdit={d.start}
      onCancel={d.cancel}
      onSave={() =>
        save({
          // One name per line, blanks dropped — the textarea is a convenience
          // over a list, and the backend stores a list.
          team_members: d.draft.team.split('\n').map((n) => n.trim()).filter(Boolean),
          report_language: d.draft.report_language,
        })
      }
      saving={saving}
      error={error}
    >
      <TextArea
        label="Your team"
        rows={4}
        value={d.draft.team}
        onChange={(e) => d.set('team', e.target.value)}
        disabled={!d.editing}
        placeholder="One name per line"
      />
      <p className={styles.hint}>
        Colleagues who join your calls. We use this to keep your own people out of a deal’s contact list, so only
        the client side is there to review.
      </p>
      <Select
        label="Summary language"
        options={REPORT_LANGUAGES.map((l) => ({ value: l.value, label: l.label }))}
        value={d.draft.report_language}
        onChange={(e) => d.set('report_language', e.target.value as ReportLanguage)}
        disabled={!d.editing}
      />
      <p className={styles.hint}>
        Call summaries, deal summaries and recommendations are written in this language. What people said is always
        quoted in the language they said it in.
      </p>
    </SettingsCard>
  )
}

export function BillingDetailsCard({ profile }: { profile: Profile }) {
  const d = useDraft({
    billing_email: profile.billing_email ?? '',
    legal_company_name: profile.legal_company_name ?? '',
    vat_id: profile.vat_id ?? '',
    billing_address: profile.billing_address ?? '',
  })
  const { save, saving, error } = useCardSave(d.done)

  return (
    <SettingsCard
      title="Billing details"
      description="Where invoices go and the details that appear on them."
      summary={[profile.billing_email, profile.vat_id ? `VAT ${profile.vat_id}` : 'VAT not set'].filter(Boolean) as string[]}
      editing={d.editing}
      onEdit={d.start}
      onCancel={d.cancel}
      onSave={() => save(d.draft)}
      saving={saving}
      error={error}
    >
      <TextField
        label="Billing email"
        value={d.draft.billing_email}
        onChange={(e) => d.set('billing_email', e.target.value)}
        disabled={!d.editing}
        placeholder="billing@company.com"
      />
      <TextField
        label="Legal name"
        value={d.draft.legal_company_name}
        onChange={(e) => d.set('legal_company_name', e.target.value)}
        disabled={!d.editing}
        placeholder="Registered company name"
      />
      <TextField
        label="VAT / Tax ID"
        value={d.draft.vat_id}
        onChange={(e) => d.set('vat_id', e.target.value)}
        disabled={!d.editing}
        placeholder="e.g. PL1234567890"
      />
      <TextArea
        label="Billing address"
        rows={3}
        value={d.draft.billing_address}
        onChange={(e) => d.set('billing_address', e.target.value)}
        disabled={!d.editing}
        placeholder="Street, city, postcode, country"
      />
    </SettingsCard>
  )
}
